import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";
import AliExpressCart from "./AliExpressCart";
import AliExpressOrderHistory from "./AliExpressOrderHistory";
import AliExpressOrderTracking from "./AliExpressOrderTracking";
import AliExpressReviews from "./AliExpressReviews";

export default function AliExpressFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  
  // Estados para novos componentes
  const [cartOpen, setCartOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [reviewsProductId, setReviewsProductId] = useState(null);

  const handleEditPost = (post) => {
    setSelectedProduct(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setProducts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setProducts(prev => prev.filter(p => p._id !== postId));
    setSelectedProduct(null);
  };

  const categories = [
    { id: "all", name: "Todos", icon: "🛒" },
    { id: "electronics", name: "Eletrônicos", icon: "📱" },
    { id: "fashion", name: "Moda", icon: "👗" },
    { id: "home", name: "Casa", icon: "🏠" },
    { id: "beauty", name: "Beleza", icon: "💄" },
    { id: "toys", name: "Brinquedos", icon: "🎮" },
    { id: "sports", name: "Esportes", icon: "⚽" },
    { id: "automotive", name: "Auto", icon: "🚗" },
  ];

  const fetchProducts = useCallback(async (pageNum = 1) => {
    try {
      const categoryParam = category !== "all" ? `&category=${category}` : "";
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/aliexpress?page=${pageNum}&limit=20${categoryParam}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (pageNum === 1) {
        setProducts(data.posts || []);
      } else {
        setProducts((prev) => [...prev, ...(data.posts || [])]);
      }
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching AliExpress feed:", error);
      setLoading(false);
    }
  }, [category, user.token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPage(1);
    fetchProducts();
  }, [fetchProducts]);

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(nextPage);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: price.currency || "BRL",
    }).format(price.amount);
  };

  const calculateDiscount = (original, current) => {
    if (!original || !current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  if (loading && products.length === 0) {
    return (
      <div className="ali_feed_loading">
        <div className="ali_loader"></div>
        <span>Carregando ofertas...</span>
      </div>
    );
  }

  return (
    <div className="ali_feed">
      {/* Header com ações */}
      <div className="ali_feed_header">
        <h1>🛒 AliExpress Marketplace</h1>
        <div className="ali_header_actions">
          <button className="ali_header_btn" onClick={() => setOrdersOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2M9 2h6a1 1 0 011 1v2a1 1 0 01-1 1H9a1 1 0 01-1-1V3a1 1 0 011-1z"/>
            </svg>
            Meus Pedidos
          </button>
          <button className="ali_header_btn ali_cart_btn" onClick={() => setCartOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
            </svg>
            Carrinho
          </button>
        </div>
      </div>

      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.icon} {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="ali_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
          <h3>Nenhum produto encontrado</h3>
          <p>Seja o primeiro a vender na comunidade!</p>
        </div>
      ) : (
        <>
          <div className="ali_grid">
            {products.map((product) => (
              <div
                key={product._id}
                className="ali_card"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="ali_card_image">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0].url || product.images[0]}
                      alt=""
                    />
                  ) : (
                    <div className="ali_no_image">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                    </div>
                  )}
                  {product.aliexpressData?.price?.originalAmount && (
                    <div className="ali_discount_badge">
                      -{calculateDiscount(
                        product.aliexpressData.price.originalAmount,
                        product.aliexpressData.price.amount
                      )}%
                    </div>
                  )}
                  {product.aliexpressData?.freeShipping && (
                    <div className="ali_shipping_badge">Frete Grátis</div>
                  )}
                </div>
                <div className="ali_card_info">
                  <h4 className="ali_product_name">
                    {product.aliexpressData?.productName || product.text}
                  </h4>
                  <div className="ali_price_row">
                    <span className="ali_price">
                      {formatPrice(product.aliexpressData?.price)}
                    </span>
                    {product.aliexpressData?.price?.originalAmount && (
                      <span className="ali_original_price">
                        {formatPrice({
                          amount: product.aliexpressData.price.originalAmount,
                          currency: product.aliexpressData.price.currency,
                        })}
                      </span>
                    )}
                  </div>
                  <div className="ali_card_meta">
                    {product.aliexpressData?.rating > 0 && (
                      <div className="ali_rating">
                        <svg viewBox="0 0 24 24" fill="#ffc107" width="14" height="14">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                        <span>{product.aliexpressData.rating.toFixed(1)}</span>
                      </div>
                    )}
                    {product.aliexpressData?.orders > 0 && (
                      <span className="ali_orders">
                        {product.aliexpressData.orders}+ vendidos
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/profile/${product.user.username}`}
                    className="ali_seller"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img src={product.user.picture} alt="" />
                    <span>{product.user.first_name}</span>
                  </Link>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={product}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="ali_load_more">
              <button onClick={loadMore} disabled={loading}>
                {loading ? "Carregando..." : "Ver mais produtos"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <div className="ali_modal_overlay" onClick={() => setSelectedProduct(null)}>
          <div className="ali_modal" onClick={(e) => e.stopPropagation()}>
            <button className="ali_modal_close" onClick={() => setSelectedProduct(null)}>
              ✕
            </button>
            <div className="ali_modal_content">
              <div className="ali_modal_gallery">
                {selectedProduct.images?.length > 0 ? (
                  <img
                    src={selectedProduct.images[0].url || selectedProduct.images[0]}
                    alt=""
                  />
                ) : (
                  <div className="ali_modal_no_image">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                )}
              </div>
              <div className="ali_modal_info">
                <h2>{selectedProduct.aliexpressData?.productName || selectedProduct.text}</h2>

                <div className="ali_modal_price">
                  <span className="ali_modal_current_price">
                    {formatPrice(selectedProduct.aliexpressData?.price)}
                  </span>
                  {selectedProduct.aliexpressData?.price?.originalAmount && (
                    <>
                      <span className="ali_modal_original_price">
                        {formatPrice({
                          amount: selectedProduct.aliexpressData.price.originalAmount,
                          currency: selectedProduct.aliexpressData.price.currency,
                        })}
                      </span>
                      <span className="ali_modal_discount">
                        -{calculateDiscount(
                          selectedProduct.aliexpressData.price.originalAmount,
                          selectedProduct.aliexpressData.price.amount
                        )}%
                      </span>
                    </>
                  )}
                </div>

                <div className="ali_modal_stats">
                  {selectedProduct.aliexpressData?.rating > 0 && (
                    <div className="ali_modal_rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          viewBox="0 0 24 24"
                          fill={star <= Math.round(selectedProduct.aliexpressData.rating) ? "#ffc107" : "#e0e0e0"}
                          width="18"
                          height="18"
                        >
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                      ))}
                      <span>{selectedProduct.aliexpressData.rating.toFixed(1)}</span>
                    </div>
                  )}
                  {selectedProduct.aliexpressData?.orders > 0 && (
                    <span className="ali_modal_orders">
                      {selectedProduct.aliexpressData.orders}+ pedidos
                    </span>
                  )}
                </div>

                {selectedProduct.aliexpressData?.variants?.length > 0 && (
                  <div className="ali_modal_variants">
                    <h4>Variantes</h4>
                    <div className="ali_variants_list">
                      {selectedProduct.aliexpressData.variants.map((variant, i) => (
                        <span key={i} className="ali_variant_tag">
                          {variant.name}: {variant.value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ali_modal_shipping">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                  <div>
                    <span>
                      {selectedProduct.aliexpressData?.freeShipping
                        ? "Frete Grátis"
                        : `Frete: ${formatPrice(selectedProduct.aliexpressData?.shipping)}`}
                    </span>
                    <span className="ali_shipping_time">
                      Entrega em 15-30 dias úteis
                    </span>
                  </div>
                </div>

                <Link
                  to={`/profile/${selectedProduct.user.username}`}
                  className="ali_modal_seller"
                >
                  <img src={selectedProduct.user.picture} alt="" />
                  <div>
                    <span className="ali_seller_name">
                      {selectedProduct.user.first_name} {selectedProduct.user.last_name}
                    </span>
                    <span className="ali_seller_label">Vendedor</span>
                  </div>
                </Link>

                <button className="ali_buy_btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                  Comprar Agora
                </button>

                <button 
                  className="ali_reviews_btn" 
                  onClick={() => setReviewsProductId(selectedProduct._id)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                  </svg>
                  Ver Avaliações
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Novos Componentes */}
      <AliExpressCart isOpen={cartOpen} onClose={() => setCartOpen(false)} />

      {ordersOpen && (
        <AliExpressOrderHistory 
          onClose={() => setOrdersOpen(false)}
          onViewOrder={(orderId) => {
            setOrdersOpen(false);
            setTrackingOrderId(orderId);
          }}
        />
      )}

      {trackingOrderId && (
        <AliExpressOrderTracking
          orderId={trackingOrderId}
          onClose={() => setTrackingOrderId(null)}
        />
      )}

      {reviewsProductId && (
        <AliExpressReviews
          productId={reviewsProductId}
          onClose={() => setReviewsProductId(null)}
        />
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
